import { omit } from "radash";
import { objectToSnake } from "ts-case-convert";

import type { ConsentTypes } from "@solvari/utils";

import {
  getCookie,
  getSolvariSessionId,
  isError,
  networkErrorToSentry,
} from "@solvari/utils";

import type { MarketingParams } from "@/events/marketingParamsParser";
import type { ArgusEvent, ArgusEvents } from "@/service/backendSync";

import { getArgusEvents } from "@/client/thinClient";
import { getMarketingParamsFromUrl } from "@/events/marketingParamsParser";
import { argusGetSummary } from "@/service/argus.api";
import { shouldRunClientOnly } from "@/service/backendSync";

type Metadata = MarketingParams & {
  argusUuid: string;
  cookieConsent: ConsentTypes | null;
  currentPage: string;
  currentPageReferrer: string | null;
  domain: string;
  experiments: Record<string, number | string>;
  landingPage: string | null;
  landingPageReferrer: string | null;
  userAgent: string;
};

function getFallbackMetadata(): Metadata {
  const currentPageMarketingParams = getMarketingParamsFromUrl();
  const referrerMarketingParams = document.referrer
    ? getMarketingParamsFromUrl(new URL(document.referrer))
    : null;

  const marketingParams = Object.values(currentPageMarketingParams).some(
    Boolean,
  )
    ? currentPageMarketingParams
    : (referrerMarketingParams ?? currentPageMarketingParams);

  return {
    argusUuid: getSolvariSessionId() ?? "",
    domain: window.location.hostname,
    currentPage: window.location.href,
    currentPageReferrer: document.referrer || null,
    userAgent: navigator.userAgent,
    cookieConsent: getCookie("solvariCookieConsent"),
    landingPage: null,
    landingPageReferrer: null,
    experiments: {},
    ...marketingParams,
  };
}

function isEventOfType<Type extends keyof ArgusEvents>(
  event: ArgusEvent,
  type: Type,
): event is ArgusEvent<Type> {
  return event.type === type;
}

function applyPageViewEvent(
  projection: Partial<Metadata>,
  event: ArgusEvent<"page_view">,
) {
  if (event.data.pageType === "referrer") {
    if (!projection.landingPage) {
      projection.landingPageReferrer = event.data.url;
    }
    projection.currentPageReferrer = event.data.url;
    return;
  }

  if (!projection.landingPage) {
    projection.landingPage = event.data.url;
  } else {
    projection.currentPageReferrer = projection.currentPage;
  }

  projection.domain = new URL(event.data.url).hostname;
  projection.currentPage = event.data.url;
}

function getClientSideArgusProjection(
  events: ArgusEvent[] | null = getArgusEvents(),
) {
  if (!events?.length) {
    return getFallbackMetadata();
  }
  return events.reduce((projection: Metadata, event) => {
    let result = projection;

    if (isEventOfType(event, "init")) {
      result.userAgent = event.data.userAgent;
    }

    if (isEventOfType(event, "page_view")) {
      applyPageViewEvent(result, event);
    }

    if (isEventOfType(event, "marketing_params")) {
      result = { ...result, ...event.data };
    }

    if (isEventOfType(event, "cookie_consent")) {
      result.cookieConsent = event.data.consentTypes;
    }

    if (isEventOfType(event, "google_optimize")) {
      result.experiments[event.data.experimentId] = event.data.variantId;
    }

    return result;
  }, getFallbackMetadata());
}

async function getMetadataFromArgus() {
  const uuid = getSolvariSessionId();
  if (!uuid || shouldRunClientOnly()) {
    return getClientSideArgusProjection();
  }

  const result = await argusGetSummary(uuid);
  if (isError(result)) {
    networkErrorToSentry(result, { tags: { project: "argus-fe" } });
    return getClientSideArgusProjection();
  }

  return { argusUuid: result.uuid, ...omit(result, ["uuid"]) };
}

async function getMetadataFromArgusSnakeCase() {
  return objectToSnake(await getMetadataFromArgus());
}

export type { Metadata };
export {
  getClientSideArgusProjection,
  getMetadataFromArgus,
  getMetadataFromArgusSnakeCase,
};
